import type { MustInclude, NonEmptyArray } from "../@types/helpers";

type SelectObjectKeys = keyof Pick<
  typeof Object,
  "entries" | "keys" | "values"
>;

/**
 * Helper for `typedEntries`, `typedKeys`, and `typedValues`
 */
const typedObject = <T extends object, M extends SelectObjectKeys>(
  obj: T,
  method: M
) =>
  Object[method](obj) as (M extends "entries"
    ? [keyof T, T[keyof T]]
    : M extends "values"
      ? T[keyof T]
      : keyof T)[];

/**
 * Typed alternative for Object.entries
 * @example: typedEntries(obj).find(([key, value]) // correct types ...);
 */
export const typedEntries = <T extends object>(o: T) =>
  typedObject(o, "entries");

/**
 * Typed alternative for Object.keys
 * @example: typedKeys(obj).map(key // correct type ...);
 */
export const typedKeys = <T extends object>(o: T) => typedObject(o, "keys");

/**
 * Typed alternative for Object.values
 * @example: typedValues(obj).map(key // correct type ...);
 */
export const typedValues = <T extends object>(o: T) => typedObject(o, "values");

/**
 * Enforces the creation of a complete array out of the values in a string union
 * @example
 *
 * type MyUnion = "FOO" | "BAR" | "BAZ";
 * const myUnionArray = stringToUnionArray<MyUnion>()("FOO", "BAR", "BAZ")
 */
export function stringUnionToArray<T>() {
  return <U extends NonEmptyArray<T>>(...elements: MustInclude<T, U>) =>
    elements;
}
